import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight, FiCheck, FiEye, FiFileText, FiMail, FiPaperclip, FiPhone, FiSend, FiX } from 'react-icons/fi';
import { ROUTES } from '../../../app/routes';
import { sendKanbanEmail } from '../../../services/kanbanAIService';
import { createTask, fetchPolicyFiles, fetchQuoteInsights, fetchTasks, insightStatusTone, uploadPolicyFile, type PolicyFileRecord, type QuoteInsights, type TaskRecord } from './submissionApi';
import AqSelect from './AqSelect';
import FilePreviewModal from '../../../components/ui/FilePreviewModal';
import RichTextComposer, { hasContent, toQuotedHtml } from '../../../components/ui/RichTextComposer';
import '../../../styles/AddQuotePage.css';
import '../../../styles/SubmissionCenter.css';

/**
 * Quote/policy detail — Submission Center's own skin over the same NowCerts
 * PolicyDetailList data PolicyDetailSidebarPanel (chat's right-rail panel)
 * already renders. Kept as a separate component rather than reusing that one
 * because this page has its own `.sc-panel` visual language (the same one
 * SubmissionDetailPanel used for the old sample-data flow) — the fields
 * available here are just NowCerts' real ones instead of the mock's.
 */

const AUTH_URL =
  process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com';
const PAGE_SIZE = 5;

interface LineOfBusiness { databaseId: string; lineOfBusinessName: string; }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NoteRecord = Record<string, any>;

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

// The policy-files API serves every file's Content-Type as application/octet-stream
// (S3's presigned URL forces it via response-content-type) regardless of the
// actual file — a blob built from that response carries the same wrong type,
// so the browser's PDF/image viewers won't render it inline. This looks the
// real type up from the file's own extension instead of trusting the server.
const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
  txt: 'text/plain',
  log: 'text/plain',
  csv: 'text/csv',
  md: 'text/markdown',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};
function mimeFromFileName(name: string): string | null {
  const ext = name.split('.').pop()?.toLowerCase();
  return (ext && MIME_BY_EXTENSION[ext]) || null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseListResponse<T>(json: any, key: string): { items: T[]; total: number } {
  const items = Array.isArray(json?.[key]) ? (json[key] as T[]) : [];
  const total = typeof json?.total === 'number' ? json.total : items.length;
  return { items, total };
}

interface PolicyDetail {
  databaseId: string;
  number: string | null;
  isQuote: boolean;
  effectiveDate: string | null;
  expirationDate: string | null;
  businessType: string | null;
  billingType: string | null;
  insuredDatabaseId: string;
  insuredEmail: string | null;
  insuredFirstName: string | null;
  insuredLastName: string | null;
  insuredCommercialName: string | null;
  carrierName: string | null;
  mgaName: string | null;
  totalPremium: number | null;
  totalAgencyCommission: number | null;
  changeDate: string | null;
  status: string | null;
  policyTermFormatted: string | null;
  lineOfBusinesses: LineOfBusiness[];
}

const SkeletonPanel: React.FC = () => (
  <div className="sc-panel">
    <header className="sc-panel__head">
      <div className="sc-panel__head-copy">
        <div className="sc__skel-bone" style={{ width: '40%', height: 10, marginBottom: 8 }} />
        <div className="sc__skel-bone" style={{ width: '65%', height: 18, marginBottom: 8 }} />
        <div className="sc__skel-bone" style={{ width: '30%', height: 14 }} />
      </div>
    </header>
    <div className="sc-panel__body">
      <section className="sc-panel__section">
        <div className="sc__skel-bone" style={{ width: '35%', height: 10, marginBottom: 10 }} />
        <div className="sc-panel__grid">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div className="sc__skel-bone" style={{ width: '50%', height: 9 }} />
              <div className="sc__skel-bone" style={{ width: '70%', height: 14 }} />
            </div>
          ))}
        </div>
      </section>
    </div>
  </div>
);

/** Loading placeholder for the Notes/Communication tab lists — mirrors the
 *  real .sc-panel__comm shape (avatar + head line + text line) so there's
 *  no layout shift once the real thread replaces it, same idea as
 *  OpportunityNotesPanel's own SkeletonNotes. */
const SkeletonCommList: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <ul className="sc-panel__comms">
    {Array.from({ length: count }, (_, i) => (
      <li className="sc-panel__comm sc-panel__comm--sys" key={i}>
        <span className="sc-panel__comm-avatar" aria-hidden="true" />
        <span className="sc-panel__comm-body">
          <span className="sc-panel__comm-head">
            <div className="sc__skel-bone" style={{ width: 70, height: 10 }} />
            <div className="sc__skel-bone" style={{ width: 50, height: 10 }} />
          </span>
          <div className="sc__skel-bone" style={{ width: `${70 - (i % 3) * 12}%`, height: 12, marginTop: 6 }} />
        </span>
      </li>
    ))}
  </ul>
);

/** Quoted (isQuote: false) mirrors the same cyan/orange convention the quotes
 *  table already uses; Received (isQuote: true) is the underwriter's response. */
function quoteStatusColor(isQuote: boolean): string {
  return isQuote ? 'var(--accent-orange)' : 'var(--accent-cyan)';
}

// Calling-code prefix for the Text tab's "To" field — the input itself only
// holds the national number, this is prepended when the SMS is actually sent.
const COUNTRY_CALLING_CODES = [
  { code: '+1', label: 'US/Canada (+1)' },
  { code: '+91', label: 'India (+91)' },
  { code: '+44', label: 'UK (+44)' },
  { code: '+61', label: 'Australia (+61)' },
  { code: '+971', label: 'UAE (+971)' },
  { code: '+63', label: 'Philippines (+63)' },
];

const PERSONAL_LOBS = ['Personal Auto', 'Homeowners'];
const TASK_STATUS_OPTIONS = ['Not Started', 'In Progress', 'Done', 'Closed'];
// AMS accepts these status values without spaces — the dropdown still shows
// the spaced label, this just maps at the API boundary in both directions.
const TASK_STATUS_API_VALUES: Record<string, string> = {
  'Not Started': 'NotStarted',
  'In Progress': 'InProgress',
  'Done': 'Done',
  'Closed': 'Closed',
};
const TASK_STATUS_DISPLAY: Record<string, string> = Object.fromEntries(
  Object.entries(TASK_STATUS_API_VALUES).map(([label, value]) => [value, label]),
);
const taskStatusDisplay = (raw: string): string => TASK_STATUS_DISPLAY[raw] || raw;
const TASK_PRIORITY_OPTIONS = ['Low', 'Medium', 'High'];

interface ProgramAction { label: string; hint: string; route: string; primary?: boolean; }

/** Which programs make sense next — same idea as the old sample-data panel's
 *  programsFor, adapted to what's actually known here: line of business decides
 *  the rating program. isQuote (terms in hand or not) previously decided whether
 *  Build proposal showed at all — that branch is dropped for now so it always
 *  shows, regardless of quote status. */
function programsFor(lob: string, _isQuote: boolean): ProgramAction[] {
  const isPersonal = PERSONAL_LOBS.indexOf(lob) !== -1;
  const rating: ProgramAction = {
    label: isPersonal ? 'Open in Personal Lines Rating' : 'Open in Commercial Rating',
    hint: 'Re-rate this risk or adjust the submission',
    route: isPersonal ? ROUTES.RATE_QUOTING : ROUTES.COMMERCIAL_LINES_RATING,
  };
  const proposal: ProgramAction = {
    label: 'Build proposal',
    hint: 'Take these terms into Quote Proposal',
    route: ROUTES.QUOTE_PROPOSAL,
  };
  const download: ProgramAction = {
    label: 'Carrier Download',
    hint: 'Pull the policy data down into the AMS',
    route: ROUTES.CARRIER_DOWNLOAD,
  };
  return [{ ...proposal, primary: true }, rating, download];
}

/** Initials for the note-thread avatar — same idea as the reference panel's
 *  `initials(who)`, just tolerant of a missing/blank author. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'N';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Logged-in user's display name — same fallback chain used for the optimistic
 *  note row (firstName + lastName, else userName, else 'You') so the
 *  Communication tab's avatar/name shows the real user like Notes does,
 *  instead of a hardcoded "You"/"YO". */
function currentUserDisplayName(): string {
  return (
    [localStorage.getItem('firstName'), localStorage.getItem('lastName')].filter(Boolean).join(' ') ||
    localStorage.getItem('userName') || 'You'
  );
}

/** "3d ago" style relative label for the Rate summary's Last activity row. */
function daysAgoLabel(dateStr: string | null): string {
  if (!dateStr) return '—';
  const then = new Date(dateStr).getTime();
  if (isNaN(then)) return '—';
  const days = Math.max(0, Math.round((Date.now() - then) / 86400000));
  return days === 0 ? 'today' : `${days}d ago`;
}

type ComposeChannel = 'Note' | 'Email' | 'Text';
/** Who a channel message goes to — the client, or the market's underwriter. */
type ComposeParty = 'client' | 'uw';
type ComposeMode = 'compose' | 'forward';

/** Email/Text sends have nowhere real to persist to yet (no comms-history
 *  endpoint confirmed for this entity) — kept client-side, same as how a
 *  freshly-added Note is optimistically pushed onto its own list below. */
interface LocalComm {
  who: string;
  channel: 'Email' | 'Text';
  txt: string;
  when: string;
  html?: boolean;
  failed?: boolean;
}

interface ThreadItem {
  key: string;
  who: string;
  channel: ComposeChannel;
  when: string;
  txt: string;
  html?: boolean;
  failed?: boolean;
}

const QuotePolicyDetailPanel: React.FC<{ policyId: string; opportunityId?: string }> = ({ policyId, opportunityId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<PolicyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<NoteRecord[]>([]);
  const [noteLoad, setNoteLoad] = useState(true);
  const [noteTotal, setNoteTotal] = useState(0);
  const [noteSkip, setNoteSkip] = useState(0);
  const [noteMoreLoad, setNoteMoreLoad] = useState(false);
  // Still feeds Forward's quoted thread (below) — Notes & Communication's own
  // visible list no longer renders from this; the Communication tab reads from
  // the real persisted history (fetchCommunicationHistory) instead.
  const [localComms, setLocalComms] = useState<LocalComm[]>([]);

  // "Notes" vs "Communication" tab — Communication is backed by the real
  // GET api/OpportunitiesSubmission/get-communication-history now that it
  // exists, fetched lazily the first time that tab is opened.
  const [activeThreadTab, setActiveThreadTab] = useState<'notes' | 'communication'>('notes');
  interface CommunicationHistoryItem {
    id: number;
    type: string;
    to: string;
    subject: string;
    description: string;
    status?: string;
    created?: string;
  }
  const [commHistory, setCommHistory] = useState<CommunicationHistoryItem[]>([]);
  const [commLoad, setCommLoad] = useState(false);
  const [commError, setCommError] = useState<string | null>(null);
  const commFetchedRef = useRef<string | null>(null);
  const fetchCommunicationHistory = () => {
    if (!opportunityId || !policyId) return;
    setCommLoad(true);
    setCommError(null);
    const authToken = localStorage.getItem('authToken') || '';
    fetch(
      `${AUTH_URL}/api/OpportunitiesSubmission/get-communication-history?opportunityId=${encodeURIComponent(opportunityId)}&quoteId=${encodeURIComponent(policyId)}`,
      { headers: { Authorization: `Bearer ${authToken}` } },
    )
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((json) => setCommHistory(Array.isArray(json?.data) ? json.data : []))
      .catch(() => setCommError('Failed to load communication history.'))
      .finally(() => setCommLoad(false));
  };
  const selectThreadTab = (tab: 'notes' | 'communication') => {
    setActiveThreadTab(tab);
    if (tab === 'communication' && commFetchedRef.current !== policyId) {
      commFetchedRef.current = policyId;
      fetchCommunicationHistory();
    }
  };
  const [policyFiles, setPolicyFiles] = useState<PolicyFileRecord[]>([]);
  const [filesLoad, setFilesLoad] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The list's downloadUrl is a presigned S3 link with response-content-disposition
  // set to attachment — opened directly (window.open/<a>) it forces a download
  // instead of a preview. Fetching it as a blob first and handing the resulting
  // blob: URL to FilePreviewModal sidesteps that header entirely (it only governs
  // how a browser handles a navigated-to response, not a same-page blob).
  const [previewFile, setPreviewFile] = useState<{ name: string; url: string } | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);

  const closePreview = () => {
    if (previewFile) URL.revokeObjectURL(previewFile.url);
    setPreviewFile(null);
  };

  const handleViewFile = async (f: PolicyFileRecord) => {
    const url: string | undefined = f.downloadUrl;
    const name: string = f.fileName || f.name || f.originalFileName || f.documentName || 'Untitled file';
    const fileKey = f.databaseId || f.id || name;
    if (!url || previewLoadingId) return;

    // FilePreviewModal renders .doc/.docx through Microsoft's Office web viewer,
    // which fetches the file itself from a URL it can reach — a blob: URL only
    // exists inside this tab, so that path needs the original presigned link,
    // not a locally-fetched blob (the attachment-disposition issue below doesn't
    // apply here since we're never iframing the S3 URL directly).
    if (/\.docx?$/i.test(name)) {
      setPreviewFile({ name, url });
      return;
    }

    setPreviewLoadingId(fileKey);
    try {
      // Fetched through our own API (same origin the rest of this panel already
      // calls) instead of the raw S3 link directly — that link's presigned
      // response-content-disposition forces a download, and in a deployed
      // environment the browser may also flat-out refuse the cross-origin S3
      // request (CORS/CSP) where localhost happened not to. The proxy strips
      // the forced disposition and returns the real content type itself, so
      // the client-side mime override below is just a defensive fallback.
      const proxyUrl = `${AUTH_URL}/api/AMPNowcerts/policy-file-proxy?url=${encodeURIComponent(url)}&fileName=${encodeURIComponent(name)}`;
      const res = await fetch(proxyUrl, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const rawBlob = await res.blob();
      const mime = mimeFromFileName(name) || rawBlob.type || 'application/octet-stream';
      const blob = mime === rawBlob.type ? rawBlob : new Blob([rawBlob], { type: mime });
      const blobUrl = URL.createObjectURL(blob);
      setPreviewFile({ name, url: blobUrl });
    } catch {
      // Network failure — fall back to the original behavior (opens/downloads
      // in a new tab) rather than a dead button.
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const [tasks, setTasks] = useState<TaskRecord[]>([]);
  const [tasksLoad, setTasksLoad] = useState(true);
  const [addingTask, setAddingTask] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskStatus, setTaskStatus] = useState('');
  const [taskPriority, setTaskPriority] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [savingTaskId, setSavingTaskId] = useState<string | null>(null);
  const [taskTotal, setTaskTotal] = useState(0);
  const [taskSkip, setTaskSkip] = useState(0);
  const [taskMoreLoad, setTaskMoreLoad] = useState(false);

  const [quoteInsights, setQuoteInsights] = useState<QuoteInsights | null>(null);
  const [insightsLoad, setInsightsLoad] = useState(true);
  const [insightsError, setInsightsError] = useState(false);

  useEffect(() => {
    if (!opportunityId) { setInsightsLoad(false); return; }
    let cancelled = false;
    setInsightsLoad(true);
    setInsightsError(false);
    fetchQuoteInsights(opportunityId, policyId)
      .then((data) => { if (!cancelled) setQuoteInsights(data); })
      .catch(() => { if (!cancelled) setInsightsError(true); })
      .finally(() => { if (!cancelled) setInsightsLoad(false); });
    return () => { cancelled = true; };
  }, [policyId, opportunityId]);

  const [composeChannel, setComposeChannel] = useState<ComposeChannel>('Note');
  const [composeMode, setComposeMode] = useState<ComposeMode>('compose');
  const [composeParty, setComposeParty] = useState<ComposeParty>('client');
  // null = "not yet hand-edited" — derive live from the selected party/channel.
  const [composeToOverride, setComposeToOverride] = useState<string | null>(null);
  // Text tab only — composeTo/composeToOverride holds just the national number;
  // this is the calling-code prefix picked alongside it.
  const [phoneCountryCode, setPhoneCountryCode] = useState(COUNTRY_CALLING_CODES[0].code);
  const [composeCc, setComposeCc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeText, setComposeText] = useState('');
  const [sending, setSending] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [composeSuccess, setComposeSuccess] = useState<string | null>(null);

  // Checked once whenever the Text tab is selected — GET /api/ampnowcerts/sms-details
  // tells us whether this agency actually has an SMS provider connected. Only once
  // isSuccessful comes back true do the To field and message box show at all.
  const [smsCheckLoading, setSmsCheckLoading] = useState(false);
  const [smsCheckError, setSmsCheckError] = useState<string | null>(null);
  const [smsReady, setSmsReady] = useState(false);
  const checkSmsDetails = async () => {
    setSmsCheckLoading(true);
    setSmsCheckError(null);
    setSmsReady(false);
    try {
      const authToken = localStorage.getItem('authToken') || '';
      const amsToken = localStorage.getItem('amsToken') || '';
      // Backend signature is GetSmsDetails(string? nowCertsToken) — it reads the
      // NowCerts token from the query string, not just the NCToken header.
      const res = await fetch(
        `${AUTH_URL}/api/ampnowcerts/sms-details?nowCertsToken=${encodeURIComponent(amsToken)}`,
        { headers: { Authorization: `Bearer ${authToken}`} },
      );
      const json = await res.json().catch(() => null);
      if (res.ok && json?.isSuccessful) {
        setSmsReady(true);
      } else {
        // Success responses carry errorMessage; a bad-request response instead
        // comes back as { message: "..." } (e.g. "SMS settings not found for
        // the current user.") — check both.
        setSmsCheckError(json?.message || "SMS settings not found for the current user.");
      }
    } catch {
      setSmsCheckError('Failed to check SMS availability.');
    } finally {
      setSmsCheckLoading(false);
    }
  };

  useEffect(() => {
    if (!policyId) return;
    setLoading(true);
    setError(null);
    setData(null);
    setNotes([]);
    setNoteTotal(0);
    setNoteSkip(0);
    setNoteLoad(true);
    setLocalComms([]);
    setActiveThreadTab('notes');
    setCommHistory([]);
    setCommError(null);
    commFetchedRef.current = null;
    setPolicyFiles([]);
    setFilesLoad(true);
    setUploadError(null);
    setTasks([]);
    setTasksLoad(true);
    setTaskTotal(0);
    setTaskSkip(0);
    setAddingTask(false);
    setTaskTitle('');
    setTaskStatus('');
    setTaskPriority('');
    setTaskDueDate('');
    setTaskError(null);
    setEditingTaskId(null);
    setSavingTaskId(null);
    setComposeChannel('Note');
    setComposeMode('compose');
    setComposeError(null);
    setComposeText('');

    const authToken = localStorage.getItem('authToken') || '';
    const amsToken = localStorage.getItem('amsToken') || '';

    fetch(
      `${AUTH_URL}/api/ampnowcerts/policydetails?databaseId=${encodeURIComponent(policyId)}&nowCertsToken=${encodeURIComponent(amsToken)}`,
      { headers: { Authorization: `Bearer ${authToken}` } },
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const json = await res.json();
        const item = Array.isArray(json) ? json[0] : json;
        if (!item) throw new Error('No data found');
        setData(item);

        const insuredDatabaseId = item.insuredDatabaseId;
        if (insuredDatabaseId) {
          fetch(
            `${AUTH_URL}/api/ampnowcerts/insured/${encodeURIComponent(insuredDatabaseId)}/notes?policyDatabaseId=${encodeURIComponent(policyId)}&skip=0&top=${PAGE_SIZE}`,
            { headers: { Authorization: `Bearer ${authToken}`, NCToken: amsToken } },
          )
            .then((r) => (r.ok ? r.json() : null))
            .then((noteJson) => {
              const { items, total } = parseListResponse<NoteRecord>(noteJson, 'notes');
              setNotes(items);
              setNoteTotal(total);
              setNoteSkip(items.length);
            })
            .catch(() => {})
            .finally(() => setNoteLoad(false));

          fetchPolicyFiles(insuredDatabaseId, policyId)
            .then(setPolicyFiles)
            .catch(() => {})
            .finally(() => setFilesLoad(false));

          fetchTasks(insuredDatabaseId, item.number || undefined, 0, PAGE_SIZE)
            .then(({ tasks: page, total }) => {
              setTasks(page);
              setTaskTotal(total);
              setTaskSkip(page.length);
            })
            .catch(() => {})
            .finally(() => setTasksLoad(false));
        } else {
          setNoteLoad(false);
          setFilesLoad(false);
          setTasksLoad(false);
        }
      })
      .catch((e) => setError(e.message || 'Failed to load policy details'))
      .finally(() => setLoading(false));
  }, [policyId]);

  const handleLoadMoreNotes = () => {
    if (noteMoreLoad || !data?.insuredDatabaseId || notes.length >= noteTotal) return;
    setNoteMoreLoad(true);
    const authToken = localStorage.getItem('authToken') || '';
    const amsToken = localStorage.getItem('amsToken') || '';
    fetch(
      `${AUTH_URL}/api/ampnowcerts/insured/${encodeURIComponent(data.insuredDatabaseId)}/notes?policyDatabaseId=${encodeURIComponent(policyId)}&skip=${noteSkip}&top=${PAGE_SIZE}`,
      { headers: { Authorization: `Bearer ${authToken}`, NCToken: amsToken } },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        const { items, total } = parseListResponse<NoteRecord>(json, 'notes');
        setNotes((prev) => [...prev, ...items]);
        setNoteTotal(total);
        setNoteSkip((prev) => prev + items.length);
      })
      .catch(() => {})
      .finally(() => setNoteMoreLoad(false));
  };

  const handleLoadMoreTasks = () => {
    if (taskMoreLoad || !data?.insuredDatabaseId || tasks.length >= taskTotal) return;
    setTaskMoreLoad(true);
    fetchTasks(data.insuredDatabaseId, data.number || undefined, taskSkip, PAGE_SIZE)
      .then(({ tasks: page, total }) => {
        setTasks((prev) => [...prev, ...page]);
        setTaskTotal(total);
        setTaskSkip((prev) => prev + page.length);
      })
      .catch(() => {})
      .finally(() => setTaskMoreLoad(false));
  };

  if (loading) return <SkeletonPanel />;

  if (error) {
    return <p className="sc-panel__note">{error}</p>;
  }

  if (!data) return null;

  const clean = (v: string | null | undefined) => (!v || v === 'null' || v.trim() === '' ? '' : v.trim());
  const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
  const fmtCurrency = (n: number | null) => (n != null ? `$${n.toLocaleString()}` : '—');

  const insuredName =
    clean(data.insuredCommercialName) ||
    [clean(data.insuredFirstName), clean(data.insuredLastName)].filter(Boolean).join(' ') ||
    'Unknown';
  const lob = data.lineOfBusinesses?.[0]?.lineOfBusinessName || '';
  const policyNumber = clean(data.number) || data.databaseId.slice(0, 8);
  const statusLabel = data.isQuote ? 'Received' : 'Quoted';

  const commissionPct =
    data.totalAgencyCommission != null && data.totalPremium
      ? `${((data.totalAgencyCommission / data.totalPremium) * 100).toFixed(0)}%`
      : '—';

  const clientContactEmail = clean(data.insuredEmail);
  const carrierLabel = clean(data.carrierName) || policyNumber;
  const defaultSubject = `${carrierLabel} — ${insuredName}`;

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !data.insuredDatabaseId) return;
    setUploadingFile(true);
    setUploadError(null);
    try {
      await uploadPolicyFile(data.insuredDatabaseId, policyId, file);
      const refreshed = await fetchPolicyFiles(data.insuredDatabaseId, policyId);
      setPolicyFiles(refreshed);
    } catch {
      setUploadError('Failed to attach the file. Please try again.');
    } finally {
      setUploadingFile(false);
    }
  };

  const taskFormValid = !!(taskTitle.trim() && taskStatus.trim() && taskPriority.trim() && taskDueDate);
  const handleAddTask = async () => {
    if (!taskFormValid || taskSaving) return;
    setTaskSaving(true);
    setTaskError(null);
    try {
      await createTask({
        title: taskTitle.trim(),
        status: TASK_STATUS_API_VALUES[taskStatus.trim()] || taskStatus.trim(),
        priority: taskPriority.trim(),
        dueDate: taskDueDate,
        policyDatabaseId: policyId,
        insuredDatabaseId: data.insuredDatabaseId || undefined,
      });
      if (data.insuredDatabaseId) {
        // Re-fetches at least as many as were already visible (not just page 1)
        // so a task added while a later page was loaded doesn't visually
        // collapse the list back down to the first PAGE_SIZE.
        const { tasks: page, total } = await fetchTasks(data.insuredDatabaseId, data.number || undefined, 0, Math.max(tasks.length, PAGE_SIZE));
        setTasks(page);
        setTaskTotal(total);
        setTaskSkip(page.length);
      }
      setTaskTitle('');
      setTaskStatus('');
      setTaskPriority('');
      setTaskDueDate('');
      setAddingTask(false);
    } catch {
      setTaskError('Failed to create the task. Please try again.');
    } finally {
      setTaskSaving(false);
    }
  };

  // Zapier/InsertTask is a full-record webhook like InsertOpportunity — updating
  // just the status still means resending the task's other fields, keyed off its
  // real databaseId instead of the placeholder GUID used for brand-new tasks.
  // Safe for tasks created through this same UI (we never set NowCerts' other
  // optional task fields from here to begin with).
  const handleTaskStatusChange = async (t: TaskRecord, taskId: string, newStatusLabel: string) => {
    const newStatusValue = TASK_STATUS_API_VALUES[newStatusLabel] || newStatusLabel;
    const currentStatusValue = t.status || '';
    if (!taskId || newStatusValue === currentStatusValue) { setEditingTaskId(null); return; }
    setSavingTaskId(taskId);
    try {
      await createTask({
        databaseId: taskId,
        title: t.title || t.name || '',
        status: newStatusValue,
        priority: t.priority || '',
        dueDate: t.dueDate || t.due_date || '',
        policyDatabaseId: policyId,
        insuredDatabaseId: data.insuredDatabaseId || undefined,
      });
      if (data.insuredDatabaseId) {
        const { tasks: page, total } = await fetchTasks(data.insuredDatabaseId, data.number || undefined, 0, Math.max(tasks.length, PAGE_SIZE));
        setTasks(page);
        setTaskTotal(total);
        setTaskSkip(page.length);
      }
      setEditingTaskId(null);
    } catch {
      // Left in edit mode (savingTaskId still clears below) so the failed pick stays visible to retry.
    } finally {
      setSavingTaskId(null);
    }
  };

  // No real underwriter contact on the policy yet — the chip stays visible
  // (matches the reference panel) but disabled, same convention as the
  // Contacts section's "—" placeholder above.
  const uwUsable = false;
  const partyEmail = composeParty === 'client' ? clientContactEmail : '';
  const partyPhone = ''; // no phone field on either party yet
  const partyName = composeParty === 'client' ? insuredName : '—';
  const composeTo = composeToOverride ?? (composeChannel === 'Text' ? partyPhone : partyEmail);

  const noteThread: ThreadItem[] = notes.map((n, i) => {
    const subject = stripHtml(n.subject || '') || 'Untitled note';
    const text = stripHtml(n.note || n.description || n.notesDescription || n.text || '');
    return {
      key: `note-${i}`,
      who: n.createdBy || n.userName || n.author || 'Note',
      channel: 'Note',
      when: n.changeDate || n.createdDate || n.date || '',
      txt: [subject, text].filter(Boolean).join(' — '),
    };
  });
  const localThread: ThreadItem[] = localComms.map((c, i) => ({
    key: `local-${i}`,
    who: c.who,
    channel: c.channel,
    when: c.when,
    txt: c.txt,
    html: c.html,
    failed: c.failed,
  }));
  const thread: ThreadItem[] = [...localThread, ...noteThread].sort(
    (a, b) => (Date.parse(b.when) || 0) - (Date.parse(a.when) || 0),
  );

  const selectChannel = (ch: ComposeChannel) => {
    setComposeMode('compose');
    setComposeChannel(ch);
    setComposeToOverride(null);
    setComposeError(null);
    setComposeSuccess(null);
    // Email's draft is HTML; Note/Text are plain text — carrying one over to
    // the other would either show raw markup or lose formatting silently.
    setComposeText('');
    if (ch === 'Text') checkSmsDetails();
  };
  const selectParty = (p: ComposeParty) => {
    setComposeParty(p);
    setComposeToOverride(null);
  };

  const buildForwardQuote = () =>
    thread.map((c) => `${c.who} — ${c.channel} — ${fmtDate(c.when)}:\n${c.txt}`).join('\n\n');

  const selectForward = () => {
    setComposeMode('forward');
    setComposeChannel('Email');
    setComposeToOverride(null);
    setComposeCc('');
    setShowCc(false);
    setComposeSubject(`Fwd: ${defaultSubject}`);
    setComposeText(`<p></p>${toQuotedHtml(buildForwardQuote())}`);
    setComposeError(null);
  };

  // Fire-and-forget log of a send attempt — POST
  // api/OpportunitiesSubmission/insert-communication-history. Description
  // carries what the user typed; ResponseJson carries the send API's own raw
  // response JSON (send-mail's or send-sms's); Status is "SUCCESS"/"FAIL" so
  // a failed send still leaves a row instead of only ever recording
  // successes. Never awaited by the caller and never surfaces an error to
  // the user - a failure here shouldn't undo the fact that the message
  // itself already sent (or failed to send) already resolved on its own.
  const logCommunicationHistory = (
    type: 'SMS' | 'EMAIL',
    to: string,
    subject: string,
    description: string,
    sendResponse: unknown,
    status: 'SUCCESS' | 'FAIL',
  ) => {
    const authToken = localStorage.getItem('authToken') || '';
    fetch(`${AUTH_URL}/api/OpportunitiesSubmission/insert-communication-history`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        QuoteId: policyId,
        InsuredId: data?.insuredDatabaseId || undefined,
        OpportunityId: opportunityId,
        Type: type,
        To: to,
        Subject: subject,
        Description: description,
        ResponseJson: JSON.stringify(sendResponse ?? {}),
        Status: status,
      }),
    })
      .then(() => {
        // Refresh the Communication tab so the just-sent message shows up
        // immediately — only if that tab has actually been opened before,
        // so this doesn't fetch it unnecessarily while still on Notes.
        if (commFetchedRef.current === policyId) fetchCommunicationHistory();
      })
      .catch((err) => console.log('insert-communication-history error:', err));
  };

  const sendCompose = async () => {
    if (sending) return;
    const composingRich = composeChannel === 'Email';
    if (composingRich ? !hasContent(composeText) : !composeText.trim()) return;
    const txt = composeText.trim();
    setSending(true);
    setComposeError(null);
    setComposeSuccess(null);
    const now = new Date().toISOString();

    if (composeChannel === 'Note') {
      if (!data.insuredDatabaseId) {
        setComposeError('No insured on file to attach this note to.');
        setSending(false);
        return;
      }
      try {
        const authToken = localStorage.getItem('authToken') || '';
        const amsToken = localStorage.getItem('amsToken') || '';
        const res = await fetch(`${AUTH_URL}/api/ampnowcerts/insured/${encodeURIComponent(data.insuredDatabaseId)}/notes`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}`, NCToken: amsToken, 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject: txt, policyDatabaseId: policyId }),
        });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        // Matches what a refetch would return (notes carry createdBy) — without
        // it here, the optimistic row falls through noteThread's fallback chain
        // to the literal string 'Note', whose initials render as "NO" until
        // the next real fetch replaces it with the actual author's "DU" etc.
        const currentUserName =
          [localStorage.getItem('firstName'), localStorage.getItem('lastName')].filter(Boolean).join(' ') ||
          localStorage.getItem('userName') || 'You';
        setNotes((prev) => [{ subject: txt, changeDate: now, createdBy: currentUserName }, ...prev]);
        setNoteTotal((prev) => prev + 1);
        setNoteSkip((prev) => prev + 1);
      } catch {
        setComposeError('Failed to save note.');
        setSending(false);
        return;
      }
    } else if (composeChannel === 'Email') {
      if (!composeTo.trim()) {
        setComposeError('Add an email address to send to.');
        setSending(false);
        return;
      }
      const isForward = composeMode === 'forward';
      const emailSubject = composeSubject.trim() || (isForward ? `Fwd: ${defaultSubject}` : defaultSubject);
      try {
        const emailRes = await sendKanbanEmail({
          to: composeTo.trim(),
          cc: composeCc.trim() || undefined,
          subject: emailSubject,
          body: txt,
        });
        logCommunicationHistory('EMAIL', composeTo.trim(), emailSubject, txt, emailRes, 'SUCCESS');
        setLocalComms((prev) => [
          { who: 'You', channel: 'Email', txt: isForward ? `Forwarded thread to ${composeTo.trim()}` : txt, when: now, html: !isForward },
          ...prev,
        ]);
        setShowCc(false);
        setComposeCc('');
        if (isForward) {
          setComposeMode('compose');
          setComposeSubject(defaultSubject);
        }
      } catch (err) {
        logCommunicationHistory('EMAIL', composeTo.trim(), emailSubject, txt, err, 'FAIL');
        setComposeError('Email failed to send — try again.');
        setSending(false);
        return;
      }
    } else {
      if (!composeTo.trim()) {
        setComposeError('Add a phone number to text.');
        setSending(false);
        return;
      }
      // POST /api/ampnowcerts/send-sms — status: 1 means it actually sent (or
      // an existing subscription already covers this number, per its own
      // message); anything else is treated as a failure, using the response's
      // own message when it has one.
      let sentOk = false;
      let failMessage = 'Failed to send text.';
      // Full E.164-ish number — composeTo is just the national number, the
      // picked calling code (phoneCountryCode) goes in front of it here.
      const fullPhoneNumber = `${phoneCountryCode}${composeTo.trim().replace(/\D/g, '')}`;
      let smsResponseJson: unknown = null;
      try {
        const authToken = localStorage.getItem('authToken') || '';
        const amsToken = localStorage.getItem('amsToken') || '';
        const res = await fetch(`${AUTH_URL}/api/ampnowcerts/send-sms`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}`, NCToken: amsToken, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ToPhoneNumber: fullPhoneNumber,
            TextMessage: txt,
            SubscriptionResponseWebhookUrl: 'https://demo.com',
          }),
        });
        const json = await res.json().catch(() => null);
        smsResponseJson = json;
        sentOk = json?.status === 1;
        if (!sentOk) failMessage = json?.message || failMessage;
      } catch {
        sentOk = false;
      }
      setLocalComms((prev) => [{ who: 'You', channel: 'Text', txt, when: now, failed: !sentOk }, ...prev]);
      // SMS has no subject field in the UI — Subject stays empty, the
      // typed message goes into Description like every other channel.
      // Logged either way, so a failed send still leaves a history row.
      logCommunicationHistory('SMS', fullPhoneNumber, '', txt, smsResponseJson, sentOk ? 'SUCCESS' : 'FAIL');
      if (sentOk) {
        setComposeSuccess('Message sent successfully.');
      } else {
        setComposeError(failMessage);
      }
    }
    setComposeText('');
    setSending(false);
  };

  return (
    <div className="sc-panel">
      <header className="sc-panel__head">
        <div className="sc-panel__head-copy">
          <div className="sc-panel__eyebrow">
            {insuredName}
            {lob && <><span className="sc-sep">·</span>{lob}</>}
          </div>
          <h2 className="sc-panel__title">{clean(data.carrierName) || policyNumber}</h2>
          <div className="sc-panel__meta">
            <span className="sc-tag">
              <i className="sc-dot" style={{ background: quoteStatusColor(data.isQuote) }} />
              {statusLabel}
            </span>
            <span className="sc-panel__ref">{policyNumber}</span>
          </div>
        </div>
      </header>

      <div className="sc-panel__body">
        <div className="sc-panel__columns">
          {/* Deal facts: what it's worth, financing/stage, subjectivities,
              who's involved, and where to go next. */}
          <div className="sc-panel__col sc-panel__col--main">
            <section className="sc-panel__section">
              <h3 className="sc-panel__section-title">Rate summary</h3>
              <div className="sc-panel__grid">
                <div><span className="sc-panel__l">Premium</span><span className="sc-panel__v">{fmtCurrency(data.totalPremium)}</span></div>
                <div><span className="sc-panel__l">Commission</span><span className="sc-panel__v">{commissionPct}</span></div>
                <div><span className="sc-panel__l">Limits</span><span className="sc-panel__v">—</span></div>
                <div><span className="sc-panel__l">Deductible</span><span className="sc-panel__v">—</span></div>
                <div><span className="sc-panel__l">Valid until</span><span className="sc-panel__v">{fmtDate(data.expirationDate)}</span></div>
                <div><span className="sc-panel__l">Last activity</span><span className="sc-panel__v sc-sla--ok">{daysAgoLabel(data.changeDate)}</span></div>
              </div>
            </section>

            {/* No financing/payment/stage model exists in NowCerts' policy data
                yet — shown, not omitted, so the layout matches the reference panel.
                "Needs premium financing" is derived (premium over $2,500), not a
                real stored flag — read-only, but `disabled` renders it greyed out
                regardless of accent-color, so it's blocked from toggling instead
                (onClick/onKeyDown preventDefault) to keep the purple checked style. */}
            <section className="sc-panel__section">
              <h3 className="sc-panel__section-title">Financing &amp; stage</h3>
              <label className="sc-panel__checkbox">
                <input
                  type="checkbox"
                  checked={(data.totalPremium ?? 0) > 2500}
                  onChange={() => {}}
                  onClick={(e) => e.preventDefault()}
                  onKeyDown={(e) => e.preventDefault()}
                  aria-readonly="true"
                />
                Needs premium financing
              </label>
            </section>

            <section className="sc-panel__section">
              <div className="sc-panel__section-title-row">
                <h3 className="sc-panel__section-title">AI Insights</h3>
                {quoteInsights?.currentStatus && (() => {
                  const tone = insightStatusTone(quoteInsights.currentStatus);
                  return (
                    <span className={`sc-health${tone ? ` sc-health--${tone}` : ''}`} style={{ marginBottom: 0 }}>
                      <i className="sc-dot" />{quoteInsights.currentStatus}
                    </span>
                  );
                })()}
              </div>
              {insightsLoad ? (
                <p className="sc-panel__note">Loading AI insights…</p>
              ) : insightsError ? (
                <p className="sc-panel__note">Failed to load AI insights.</p>
              ) : !quoteInsights?.insights.length && !quoteInsights?.nextSteps.length ? (
                <p className="sc-panel__note">No AI insights yet for this quote.</p>
              ) : (
                <div className="sc-opp__insight-card">
                  {quoteInsights.insights.length > 0 && (
                    <>
                      <div className="sc-opp__insight-label">Insights</div>
                      <ul className="sc-opp__insight-list">
                        {quoteInsights.insights.map((line, i) => <li key={i}>{line}</li>)}
                      </ul>
                    </>
                  )}
                  {quoteInsights.nextSteps.length > 0 && (
                    <>
                      <div className="sc-opp__insight-label">Next steps</div>
                      <ul className="sc-opp__insight-list">
                        {quoteInsights.nextSteps.map((line, i) => <li key={i}>{line}</li>)}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </section>

            <section className="sc-panel__section">
              <div className="sc-panel__section-title-row">
                <h3 className="sc-panel__section-title">Subjectivities</h3>
                {!addingTask && (
                  <button type="button" className="sc-panel__task-btn" onClick={() => { setAddingTask(true); setTaskError(null); }}>
                    + Add task
                  </button>
                )}
              </div>

              {tasksLoad ? (
                <p className="sc-panel__note">Loading tasks…</p>
              ) : tasks.length === 0 ? (
                <p className="sc-panel__note">None recorded.</p>
              ) : (
                <ul className="sc-panel__list">
                  {tasks.map((t, i) => {
                    const taskId = t.databaseId || t.id || '';
                    const taskKey = taskId || String(i);
                    const taskTitleText = t.title || t.name || 'Untitled task';
                    const taskStatusText = taskStatusDisplay(t.status || '');
                    const taskPriorityText = t.priority || '';
                    const taskDue = t.dueDate || t.due_date || '';
                    return (
                      <li key={taskKey}>
                        <span className="sc-panel__list-icon"><FiCheck size={12} /></span>
                        <span className="sc-panel__list-main">
                          {taskTitleText}
                          <span className="sc-panel__list-meta">
                            {[taskPriorityText, taskDue ? `due ${fmtDate(taskDue)}` : ''].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        {savingTaskId === taskKey ? (
                          <span className="sc-tag">
                            <i className="sc-tag__spinner" aria-hidden="true" />
                            Updating…
                          </span>
                        ) : editingTaskId === taskKey ? (
                          <span className="sc-stage-edit">
                            <AqSelect
                              value={taskStatusText}
                              onChange={(v) => handleTaskStatusChange(t, taskId, v)}
                              options={TASK_STATUS_OPTIONS}
                              placeholder="Status…"
                              defaultOpen
                              portal
                            />
                            <button
                              type="button"
                              className="sc-stage-edit__cancel"
                              onClick={() => setEditingTaskId(null)}
                              aria-label="Cancel"
                            >
                              <FiX size={12} />
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="sc-tag sc-tag--editable"
                            onClick={() => taskId && setEditingTaskId(taskKey)}
                            disabled={!taskId}
                          >
                            {taskStatusText || 'Set status'}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {!tasksLoad && tasks.length < taskTotal && (
                <button type="button" className="sc-panel__scan-btn" onClick={handleLoadMoreTasks} disabled={taskMoreLoad}>
                  <FiCheck size={13} aria-hidden="true" />
                  {taskMoreLoad ? 'Loading…' : `Show more (${tasks.length} of ${taskTotal})`}
                </button>
              )}

              {addingTask && (
                <div className="sc-panel__note-form">
                  <input
                    className="sc-panel__note-input"
                    placeholder="Title…"
                    value={taskTitle}
                    onChange={(e) => setTaskTitle(e.target.value)}
                    autoFocus
                  />
                  <AqSelect value={taskStatus} onChange={setTaskStatus} options={TASK_STATUS_OPTIONS} placeholder="Status…" />
                  <AqSelect value={taskPriority} onChange={setTaskPriority} options={TASK_PRIORITY_OPTIONS} placeholder="Priority…" />
                  <input
                    type="date"
                    className="sc-panel__note-input"
                    value={taskDueDate}
                    onChange={(e) => setTaskDueDate(e.target.value)}
                  />
                  {taskError && <p className="sc-panel__note-error">{taskError}</p>}
                  <div className="sc-panel__note-actions">
                    <button
                      type="button"
                      className="sc-panel__note-cancel-btn"
                      onClick={() => { setAddingTask(false); setTaskError(null); }}
                      disabled={taskSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="sc-panel__note-save-btn"
                      onClick={handleAddTask}
                      disabled={taskSaving || !taskFormValid}
                    >
                      {taskSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </section>

            <section className="sc-panel__section">
              <h3 className="sc-panel__section-title">Contacts</h3>
              <div className="sc-panel__contacts">
                <div className="sc-panel__contact">
                  <span className="sc-panel__l">Underwriter</span>
                  <span className="sc-panel__v">—</span>
                </div>
                <div className="sc-panel__contact">
                  <span className="sc-panel__l">Client contact</span>
                  <span className="sc-panel__v">{insuredName}</span>
                  {clientContactEmail && (
                    <span className="sc-panel__contact-links">
                      <a href={`mailto:${clientContactEmail}`} className="sc-panel__contact-link">
                        <FiMail size={11} /> <span>{clientContactEmail}</span>
                      </a>
                    </span>
                  )}
                </div>
              </div>
            </section>

            <section className="sc-panel__section">
              <h3 className="sc-panel__section-title">Take this further</h3>
              <div className="sc-panel__actions">
                {programsFor(lob, data.isQuote).map((a) => (
                  <button
                    key={a.label}
                    type="button"
                    className={`sc-panel__action${a.primary ? ' sc-panel__action--primary' : ''}`}
                    onClick={() => navigate(a.route)}
                  >
                    <span className="sc-panel__action-copy">
                      <span className="sc-panel__action-label">{a.label}</span>
                      <span className="sc-panel__action-hint">{a.hint}</span>
                    </span>
                    <FiArrowRight size={14} aria-hidden="true" />
                  </button>
                ))}
              </div>
            </section>
          </div>

          {/* Files, then the note thread and composer right below them. */}
          <div className="sc-panel__col sc-panel__col--side">
            <section className="sc-panel__section">
              <h3 className="sc-panel__section-title">Documents</h3>
              {filesLoad ? (
                <p className="sc-panel__note">Loading documents…</p>
              ) : policyFiles.length === 0 ? (
                <p className="sc-panel__note">Nothing uploaded for this market yet.</p>
              ) : (
                <ul className="sc-panel__list">
                  {policyFiles.map((f, i) => {
                    const fileName = f.fileName || f.name || f.originalFileName || f.documentName || 'Untitled file';
                    const fileKey = f.databaseId || f.id || i;
                    const isPreviewing = previewLoadingId === (f.databaseId || f.id || fileName);
                    return (
                      <li key={fileKey}>
                        <span className="sc-panel__list-icon"><FiCheck size={12} /></span>
                        <span className="sc-panel__list-main">{fileName}</span>
                        {f.downloadUrl && (
                          <button
                            type="button"
                            className="sc-panel__task-btn"
                            onClick={() => handleViewFile(f)}
                            disabled={isPreviewing}
                            aria-label={`View ${fileName}`}
                            title="View"
                          >
                            {isPreviewing ? <i className="sc-tag__spinner" aria-hidden="true" /> : <FiEye size={12} aria-hidden="true" />}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {uploadError && <p className="sc-panel__compose-error">{uploadError}</p>}
              <button type="button" className="sc-panel__scan-btn" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}>
                <FiPaperclip size={13} aria-hidden="true" />
                {uploadingFile ? 'Uploading…' : 'Attach a file'}
              </button>
              <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelected} />
            </section>

            <section className="sc-panel__section">
              <h3 className="sc-panel__section-title">Notes &amp; communication</h3>

              <div className="sc-panel__compose-tabs" role="tablist" aria-label="Notes or communication" style={{ marginBottom: 10 }}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeThreadTab === 'notes'}
                  className={`sc-panel__compose-tab${activeThreadTab === 'notes' ? ' is-active' : ''}`}
                  onClick={() => selectThreadTab('notes')}
                >
                  <FiFileText size={12} aria-hidden="true" /> Notes
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeThreadTab === 'communication'}
                  className={`sc-panel__compose-tab${activeThreadTab === 'communication' ? ' is-active' : ''}`}
                  onClick={() => selectThreadTab('communication')}
                >
                  <FiSend size={12} aria-hidden="true" /> Communication
                </button>
              </div>

              {activeThreadTab === 'notes' ? (
                <>
                  {noteLoad ? (
                    <SkeletonCommList />
                  ) : noteThread.length === 0 ? (
                    <p className="sc-panel__note">No notes logged yet.</p>
                  ) : (
                    <ul className="sc-panel__comms">
                      {noteThread.map((c) => (
                        <li key={c.key} className="sc-panel__comm sc-panel__comm--sys">
                          <span className="sc-panel__comm-avatar">{initials(c.who)}</span>
                          <span className="sc-panel__comm-body">
                            <span className="sc-panel__comm-head">
                              <strong>{c.who}</strong>
                              <span className="sc-panel__comm-channel">
                                <FiFileText size={10} /> {c.channel}
                              </span>
                              <span className="sc-panel__comm-time">{c.when ? fmtDate(c.when) : ''}</span>
                            </span>
                            <span className="sc-panel__comm-text">{c.txt}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {!noteLoad && notes.length < noteTotal && (
                    <button type="button" className="sc-panel__scan-btn" onClick={handleLoadMoreNotes} disabled={noteMoreLoad}>
                      <FiCheck size={13} aria-hidden="true" />
                      {noteMoreLoad ? 'Loading…' : `Show more (${notes.length} of ${noteTotal})`}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {commLoad ? (
                    <SkeletonCommList />
                  ) : commError ? (
                    <p className="sc-panel__note">{commError}</p>
                  ) : commHistory.length === 0 ? (
                    <p className="sc-panel__note">No communication logged yet.</p>
                  ) : (
                    <ul className="sc-panel__comms">
                      {commHistory.map((c) => {
                        const who = currentUserDisplayName();
                        return (
                          <li key={c.id} className="sc-panel__comm sc-panel__comm--out">
                            <span className="sc-panel__comm-avatar">{initials(who)}</span>
                            <span className="sc-panel__comm-body">
                              <span className="sc-panel__comm-head">
                                <strong>{who}</strong>
                                <span className="sc-panel__comm-channel">
                                  {c.type === 'EMAIL' ? <FiMail size={10} /> : <FiPhone size={10} />} {c.type}
                                </span>
                                {c.status === 'FAIL' && (
                                  <span style={{ color: 'var(--accent-red)', fontSize: 'var(--sc-t1)', fontWeight: 600 }}>Failed</span>
                                )}
                                <span className="sc-panel__comm-time">{c.created ? fmtDate(c.created) : ''}</span>
                              </span>
                              {c.subject && <span className="sc-panel__comm-text"><strong>{c.subject}</strong></span>}
                              <span className="sc-panel__comm-text">{c.description}</span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}

              <div className="sc-panel__compose">
                <div className="sc-panel__compose-tabs" role="tablist" aria-label="Message channel">
                  {(['Note', 'Email', 'Text'] as ComposeChannel[]).map((ch) => (
                    <button
                      key={ch}
                      type="button"
                      role="tab"
                      aria-selected={composeMode === 'compose' && composeChannel === ch}
                      className={`sc-panel__compose-tab${composeMode === 'compose' && composeChannel === ch ? ' is-active' : ''}`}
                      onClick={() => selectChannel(ch)}
                    >
                      {ch === 'Note' ? <FiFileText size={12} aria-hidden="true" /> : ch === 'Email' ? <FiMail size={12} aria-hidden="true" /> : <FiPhone size={12} aria-hidden="true" />}
                      {ch}
                    </button>
                  ))}
                  {thread.length > 0 && (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={composeMode === 'forward'}
                      className={`sc-panel__compose-tab${composeMode === 'forward' ? ' is-active' : ''}`}
                      onClick={selectForward}
                    >
                      <FiSend size={12} aria-hidden="true" /> Forward
                    </button>
                  )}
                </div>

                {composeMode === 'forward' ? (
                  <p className="sc-panel__compose-hint">Forwards the whole thread above as one email — edit the note before sending.</p>
                ) : composeChannel === 'Note' ? (
                  <p className="sc-panel__compose-hint">Internal only — not sent to the client or the market.</p>
                ) : (
                  <div className="sc-panel__compose-party">
                    <span className="sc-panel__compose-party-label">Send as</span>
                    <div className="sc-panel__compose-party-options">
                      <button
                        type="button"
                        className={`sc-panel__compose-chip${composeParty === 'client' ? ' is-active' : ''}`}
                        onClick={() => selectParty('client')}
                      >
                        {insuredName}
                      </button>
                      <button
                        type="button"
                        className={`sc-panel__compose-chip${composeParty === 'uw' ? ' is-active' : ''}`}
                        onClick={() => selectParty('uw')}
                        disabled={!uwUsable}
                        title={uwUsable ? undefined : 'No underwriter on file for this market'}
                      >
                        Underwriter
                      </button>
                    </div>
                  </div>
                )}

                {composeChannel === 'Email' && (
                  <div className="sc-panel__compose-fields">
                    <label className="sc-panel__compose-field">
                      <span>To</span>
                      <input value={composeTo} onChange={(e) => setComposeToOverride(e.target.value)} placeholder="name@example.com" />
                    </label>
                    {showCc ? (
                      <label className="sc-panel__compose-field">
                        <span>Cc</span>
                        <input value={composeCc} onChange={(e) => setComposeCc(e.target.value)} placeholder="cc@example.com" />
                      </label>
                    ) : (
                      <button type="button" className="sc-panel__compose-addcc" onClick={() => setShowCc(true)}>+ Cc</button>
                    )}
                    <label className="sc-panel__compose-field">
                      <span>Subject</span>
                      <input value={composeSubject || defaultSubject} onChange={(e) => setComposeSubject(e.target.value)} />
                    </label>
                  </div>
                )}

                {composeChannel === 'Text' && (
                  <div className="sc-panel__compose-fields">
                    {smsCheckLoading ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div className="sc__skel-bone" style={{ width: '50%', height: 14 }} />
                        <div className="sc__skel-bone" style={{ width: '100%', height: 60 }} />
                      </div>
                    ) : smsCheckError ? (
                      <p className="sc-panel__compose-hint sc-panel__compose-hint--warn">
                        <FiAlertCircle size={11} aria-hidden="true" /> {smsCheckError}
                      </p>
                    ) : (
                      <label className="sc-panel__compose-field">
                        <span>To</span>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <div className="sc-panel__compose-cc" style={{ flexShrink: 0, width: 92 }}>
                            <AqSelect
                              value={phoneCountryCode}
                              onChange={setPhoneCountryCode}
                              options={COUNTRY_CALLING_CODES.map((c) => c.code)}
                              portal
                            />
                          </div>
                          <input
                            style={{ flex: 1, minWidth: 0 }}
                            value={composeTo}
                            onChange={(e) => setComposeToOverride(e.target.value)}
                            placeholder="555 555 5555"
                          />
                        </div>
                      </label>
                    )}
                  </div>
                )}

                {composeChannel === 'Email' ? (
                  <RichTextComposer
                    initialHtml={composeText}
                    resetToken={`${policyId}:${composeMode}`}
                    onChange={setComposeText}
                    placeholder={composeMode === 'forward' ? 'Add a note before the forwarded thread…' : `Write to ${partyName}…`}
                    minRows={composeMode === 'forward' ? 8 : 4}
                  />
                ) : composeChannel === 'Text' ? (
                  smsReady && (
                    <textarea
                      className="sc-panel__compose-input"
                      value={composeText}
                      onChange={(e) => setComposeText(e.target.value)}
                      placeholder={`Text ${partyName}…`}
                      rows={4}
                    />
                  )
                ) : (
                  <textarea
                    className="sc-panel__compose-input"
                    value={composeText}
                    onChange={(e) => setComposeText(e.target.value)}
                    placeholder="Add a note…"
                    rows={3}
                  />
                )}
                {composeError && <p className="sc-panel__compose-error">{composeError}</p>}
                {composeSuccess && (
                  <p className="sc-panel__compose-hint" style={{ color: 'var(--accent-green)' }}>{composeSuccess}</p>
                )}
                {(composeChannel !== 'Text' || smsReady) && (
                  <div className="sc-panel__compose-foot">
                    {composeChannel === 'Text' ? (
                      <span className="sc-panel__compose-count">
                        {composeText.length} char{composeText.length > 160 ? ` · ${Math.ceil(composeText.length / 153)} segments` : ''}
                      </span>
                    ) : <span />}
                    <button
                      type="button"
                      className="sc-panel__compose-send"
                      disabled={(composeChannel === 'Email' ? !hasContent(composeText) : !composeText.trim()) || sending}
                      onClick={sendCompose}
                    >
                      {sending ? 'Sending…' : composeMode === 'forward' ? 'Forward' : composeChannel === 'Note' ? 'Add note' : `Send ${composeChannel.toLowerCase()}`}
                    </button>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
      {previewFile && (
        <FilePreviewModal name={previewFile.name} url={previewFile.url} onClose={closePreview} />
      )}
    </div>
  );
};

export default QuotePolicyDetailPanel;
